import type { UpdateDeliveryDto } from '@repo/types';
import { DELIVERY_STATE, DeliveryState } from '@theailab-dev/parchment';
import React, { useRef, useState } from 'react';
import { getDeliveryStateLabel } from '../utils/deliveryStateLabels';

const selectClassName =
  'w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-base text-slate-800 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-100';

const inputClassName =
  'w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base text-slate-800 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-100';

//todo: 택배사 api목록으로 받아야하나 고민
const DELIVERY_CARRIER_OPTIONS: string[] = ['로젠택배'];
const EMPTY_CARRIER_VALUE = '';

const normalizeCarrier = (carrier?: string) => carrier?.trim() ?? EMPTY_CARRIER_VALUE;
const normalizeTrackingNumber = (trackingNumber?: string) => trackingNumber?.replace(/-/g, '').trim() ?? '';

const createFormState = (initial: UpdateDeliveryDto) => ({
  state: initial.state,
  carrier: normalizeCarrier(initial.carrier),
  trackingNumber: normalizeTrackingNumber(initial.trackingNumber),
});

interface DeliveryEditModalProps {
  onClose: () => void;
  initial: UpdateDeliveryDto;
  onSave: (patch: UpdateDeliveryDto) => Promise<void>;
}

const DeliveryEditModal: React.FC<DeliveryEditModalProps> = ({ onClose, initial, onSave }) => {
  const normalizedInitial = createFormState(initial);
  const [state, setState] = useState(normalizedInitial.state);
  const [carrier, setCarrier] = useState(normalizedInitial.carrier);
  const [trackingNumber, setTrackingNumber] = useState(normalizedInitial.trackingNumber);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const baselineRef = useRef(normalizedInitial);

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();

    const base = baselineRef.current;
    const normalizedTrackingNumber = normalizeTrackingNumber(trackingNumber);
    const normalizedCarrier = normalizeCarrier(carrier);
    const patch: UpdateDeliveryDto = {};

    if (state !== base.state) {
      patch.state = state;
    }
    if (normalizedCarrier !== normalizeCarrier(base.carrier)) {
      patch.carrier = normalizedCarrier;
    }
    if (normalizedTrackingNumber !== normalizeTrackingNumber(base.trackingNumber)) {
      patch.trackingNumber = normalizedTrackingNumber;
    }

    if (Object.keys(patch).length === 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSave(patch);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-opacity-50 fixed inset-0 z-50 mb-0 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 p-4">
          <h2 className="text-xl font-semibold text-slate-800">배송 정보 수정</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-slate-400 transition-colors hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-50">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4">
          <div className="space-y-4">
            <div>
              <label htmlFor="delivery-edit-state" className="mb-2 block text-sm font-medium text-slate-700">
                배송 상태
                <p className="text-xs text-slate-500">
                  (택배사와 운송장 번호를 입력하면 자동으로 <strong className="text-indigo-600">출고 완료</strong>로
                  변경됩니다.)
                </p>
              </label>
              <select
                id="delivery-edit-state"
                value={state}
                onChange={(e) => setState(e.target.value as DeliveryState)}
                className={selectClassName}>
                {Object.values(DELIVERY_STATE).map((state) => (
                  <option key={state} value={state}>
                    {getDeliveryStateLabel(state)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="delivery-edit-carrier" className="mb-2 block text-sm font-medium text-slate-700">
                택배사
              </label>
              <select
                id="delivery-edit-carrier"
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
                className={selectClassName}>
                <option value={EMPTY_CARRIER_VALUE}>없음</option>
                {DELIVERY_CARRIER_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="delivery-edit-tracking" className="mb-2 block text-sm font-medium text-slate-700">
                운송장 번호
              </label>
              <input
                id="delivery-edit-tracking"
                type="text"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(normalizeTrackingNumber(e.target.value))}
                className={inputClassName}
                placeholder="운송장 번호"
                autoComplete="off"
              />
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-lg bg-slate-200 px-4 py-2 font-medium text-slate-700 transition-colors hover:bg-slate-300 disabled:cursor-not-allowed disabled:opacity-50">
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-lg bg-indigo-600 px-4 py-2 font-medium text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50">
              {isSubmitting ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeliveryEditModal;
